import * as React from 'react';

import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import useAxios from '../utils/useAxios';
import PageShell from './layout/PageShell';
import { ShellContext } from './layout/AppShell';
import { NumberField, SectionCard, StatCard, currency, number } from './ui';
import { TRUE_COLORS } from '../theme';
import { CardView, Card } from './business/LoyaltyCard';
import { PublicBrand, useFontsStylesheet } from './business/brand';
import LoyaltyCustomers from './LoyaltyCustomers';

/** Categories and products a reward applies to (server ids). */
type Scope = { categories: number[]; products: number[] };
type Give =
  | { type: 'free_item'; quantity: number; choices: { scope: Scope; max_value: number | null }[] }
  | { type: 'percent'; percent: number; cap: number | null }
  | { type: 'two_for_one'; scope: Scope; pairs: number }
  | { type: 'gift'; text: string };
type Rules = { requires: Scope | null; give: Give[] };
type Step = { visit: number; title: string; description: string; rules: Rules };
/** An invitation reward: the friend's welcome, or the thanks for whoever invited them. */
type InviteReward = { title: string; description: string; rules: Rules };
type Invites = { enabled: boolean; per_customer: number; welcome: InviteReward; thanks: InviteReward };
type Stats = {
  members: number; visits: number; visits_7d: number; rewards_available: number; rewards_used: number; rewards_expired: number;
  invites_joined?: number; invites_bought?: number;
};
type Program = {
  name: string; active: boolean; starts_on: string | null; ends_on: string | null; min_amount: number; reward_days: number;
  running?: boolean; total_visits?: number; steps: Step[]; stats?: Stats;
  invites?: { enabled: boolean; per_customer: number; welcome: Partial<InviteReward>; thanks: Partial<InviteReward> };
};
type Draft = Pick<Program, 'name' | 'active' | 'starts_on' | 'ends_on' | 'min_amount' | 'reward_days' | 'steps'> & { invites: Invites };
type Option = { kind: 'categories' | 'products'; id: number; name: string };

const EMPTY_SCOPE: Scope = { categories: [], products: [] };

const KINDS: { type: Give['type']; label: string }[] = [
  { type: 'free_item', label: 'Producto gratis' },
  { type: 'percent', label: 'Descuento en la compra' },
  { type: 'two_for_one', label: '2x1' },
  { type: 'gift', label: 'Regalo (se entrega aparte)' },
];

const blank = (type: Give['type']): Give =>
  type === 'free_item' ? { type, quantity: 1, choices: [{ scope: EMPTY_SCOPE, max_value: null }] }
    : type === 'percent' ? { type, percent: 10, cap: null }
      : type === 'two_for_one' ? { type, scope: EMPTY_SCOPE, pairs: 1 }
        : { type, text: '' };

const toRules = (rules?: Partial<Rules>): Rules => ({ requires: rules?.requires ?? null, give: rules?.give ?? [] });
const toInviteReward = (reward?: Partial<InviteReward>): InviteReward => reward?.title
  ? { title: reward.title, description: reward.description ?? '', rules: toRules(reward.rules) }
  : { title: '', description: '', rules: { requires: null, give: [blank('free_item')] } };

const NEW_PROGRAM: Draft = {
  name: 'Programa de visitas', active: false, starts_on: null, ends_on: null, min_amount: 0, reward_days: 30, steps: [],
  invites: { enabled: false, per_customer: 3, welcome: toInviteReward(), thanks: toInviteReward() },
};

const toDraft = (program: Program | null): Draft => program
  ? { name: program.name, active: program.active, starts_on: program.starts_on, ends_on: program.ends_on, min_amount: program.min_amount, reward_days: program.reward_days,
    steps: program.steps.map(s => ({ visit: s.visit, title: s.title, description: s.description, rules: toRules(s.rules) })),
    invites: {
      enabled: !!program.invites?.enabled, per_customer: program.invites?.per_customer ?? 3,
      welcome: toInviteReward(program.invites?.welcome), thanks: toInviteReward(program.invites?.thanks),
    } }
  : NEW_PROGRAM;

const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  if (typeof data === 'string') return data;
  if (typeof data?.detail === 'string') return data.detail;
  if (data && typeof data === 'object') return Object.values(data).flat().join(' ');
  return fallback;
};

/** Categories and products in one field, grouped. */
const ScopeField = ({ label, scope, options, disabled, onChange }: {
  label: string; scope: Scope; options: Option[]; disabled: boolean; onChange: (scope: Scope) => void;
}) => {
  const value = options.filter(o => scope[o.kind].includes(o.id));
  return (
    <Autocomplete
      multiple
      size="small"
      disabled={disabled}
      options={options}
      value={value}
      groupBy={o => (o.kind === 'categories' ? 'Categorías' : 'Productos')}
      getOptionLabel={o => o.name}
      isOptionEqualToValue={(a, b) => a.kind === b.kind && a.id === b.id}
      onChange={(_, picked) => onChange({
        categories: picked.filter(o => o.kind === 'categories').map(o => o.id),
        products: picked.filter(o => o.kind === 'products').map(o => o.id),
      })}
      renderInput={params => <TextField {...params} label={label} placeholder={value.length ? '' : 'Categorías o productos'} />}
      sx={{ flex: 1, minWidth: 220 }}
    />
  );
};

/** One part of a reward: what it gives. */
const GiveEditor = ({ give, options, disabled, onChange }: { give: Give; options: Option[]; disabled: boolean; onChange: (give: Give) => void }) => {
  if (give.type === 'free_item') {
    return (
      <Stack spacing={1.25}>
        {give.choices.map((choice, i) => (
          <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
            <ScopeField label={i ? 'O de' : 'Gratis, de'} scope={choice.scope} options={options} disabled={disabled}
              onChange={scope => onChange({ ...give, choices: give.choices.map((c, j) => (j === i ? { ...c, scope } : c)) })} />
            <NumberField size="small" label="Hasta $" value={choice.max_value} disabled={disabled} sx={{ width: { sm: 170 } }}
              helperText={choice.max_value ? 'Si cuesta más, paga la diferencia' : 'Opcional'}
              onChange={v => onChange({ ...give, choices: give.choices.map((c, j) => (j === i ? { ...c, max_value: v || null } : c)) })} />
            {give.choices.length > 1 && !disabled && (
              <Tooltip title="Quitar opción">
                <IconButton size="small" onClick={() => onChange({ ...give, choices: give.choices.filter((_, j) => j !== i) })}><DeleteOutlineIcon fontSize="small" /></IconButton>
              </Tooltip>
            )}
          </Stack>
        ))}
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          {!disabled && (
            <Button size="small" startIcon={<AddIcon />} onClick={() => onChange({ ...give, choices: [...give.choices, { scope: EMPTY_SCOPE, max_value: null }] })}>
              Otra opción
            </Button>
          )}
          <NumberField size="small" label="Cantidad" value={give.quantity} decimals={false} disabled={disabled} sx={{ width: 110 }}
            onChange={v => onChange({ ...give, quantity: v ?? 0 })} />
        </Stack>
      </Stack>
    );
  }
  if (give.type === 'percent') {
    return (
      <Stack direction="row" spacing={1}>
        <NumberField size="small" label="% de descuento" value={give.percent} disabled={disabled} sx={{ width: 150 }}
          onChange={v => onChange({ ...give, percent: v ?? 0 })} />
        <NumberField size="small" label="Tope $ (opcional)" value={give.cap} disabled={disabled} sx={{ width: 170 }}
          onChange={v => onChange({ ...give, cap: v || null })} />
      </Stack>
    );
  }
  if (give.type === 'two_for_one') {
    return (
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <ScopeField label="2x1 en" scope={give.scope} options={options} disabled={disabled} onChange={scope => onChange({ ...give, scope })} />
        <NumberField size="small" label="Pares por compra" value={give.pairs} decimals={false} disabled={disabled} sx={{ width: { sm: 150 } }}
          onChange={v => onChange({ ...give, pairs: v ?? 0 })} />
      </Stack>
    );
  }
  return (
    <TextField size="small" fullWidth label="Qué se entrega" value={give.text} disabled={disabled} inputProps={{ maxLength: 120 }}
      onChange={e => onChange({ ...give, text: e.target.value })} />
  );
};

/** A step of the roadmap: the visit, what the customer reads and the rules the register applies. */
/** What a reward gives (one or more parts) and what the order must include: the rules the register applies. */
const RulesEditor = ({ rules, options, disabled, onChange }: { rules: Rules; options: Option[]; disabled: boolean; onChange: (rules: Rules) => void }) => {
  const setGive = (i: number, give: Give) => onChange({ ...rules, give: rules.give.map((g, j) => (j === i ? give : g)) });
  return (
    <>
      {rules.give.map((give, i) => (
        <Box key={i} sx={{ mt: 2, pl: 1.5, borderLeft: `3px solid ${TRUE_COLORS.borderSolid}` }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.25 }}>
            <TextField select size="small" label="Da" value={give.type} disabled={disabled} sx={{ minWidth: 230 }}
              onChange={e => setGive(i, blank(e.target.value as Give['type']))}>
              {KINDS.map(kind => <MenuItem key={kind.type} value={kind.type}>{kind.label}</MenuItem>)}
            </TextField>
            {rules.give.length > 1 && !disabled && (
              <Tooltip title="Quitar">
                <IconButton size="small" onClick={() => onChange({ ...rules, give: rules.give.filter((_, j) => j !== i) })}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          <GiveEditor give={give} options={options} disabled={disabled} onChange={g => setGive(i, g)} />
        </Box>
      ))}
      {!disabled && (
        <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1.5 }}
          onClick={() => onChange({ ...rules, give: [...rules.give, blank('gift')] })}>
          Agregar a esta recompensa
        </Button>
      )}
      <Stack direction="row" sx={{ mt: 2 }}>
        <ScopeField label="Solo si la orden incluye (opcional)" scope={rules.requires ?? EMPTY_SCOPE} options={options} disabled={disabled}
          onChange={scope => onChange({ ...rules, requires: scope.categories.length || scope.products.length ? scope : null })} />
      </Stack>
    </>
  );
};

/** An invitation reward: its name, the detail the customer reads and its rules. */
const InviteRewardEditor = ({ label, hint, reward, options, disabled, onChange }: {
  label: string; hint: string; reward: InviteReward; options: Option[]; disabled: boolean; onChange: (reward: InviteReward) => void;
}) => (
  <Box sx={{ border: `1px solid ${TRUE_COLORS.borderSolid}`, borderRadius: 2, p: 2 }}>
    <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>{hint}</Typography>
    <TextField size="small" fullWidth label="Recompensa" placeholder="Lo que lee tu cliente, p. ej. Bebida gratis" value={reward.title} disabled={disabled}
      inputProps={{ maxLength: 80 }} onChange={e => onChange({ ...reward, title: e.target.value })} />
    <TextField size="small" fullWidth label="Detalle (opcional)" value={reward.description} disabled={disabled} sx={{ mt: 1.5 }}
      inputProps={{ maxLength: 300 }} onChange={e => onChange({ ...reward, description: e.target.value })} />
    <RulesEditor rules={reward.rules} options={options} disabled={disabled} onChange={rules => onChange({ ...reward, rules })} />
  </Box>
);

const StepEditor = ({ step, options, disabled, onChange, onRemove }: {
  step: Step; options: Option[]; disabled: boolean; onChange: (step: Step) => void; onRemove: () => void;
}) => {
  return (
    <Box data-testid="loyalty-step" sx={{ border: `1px solid ${TRUE_COLORS.borderSolid}`, borderRadius: 2, p: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <NumberField size="small" label="Visita" value={step.visit} decimals={false} disabled={disabled} sx={{ width: 90, flexShrink: 0 }}
          onChange={v => onChange({ ...step, visit: v ?? 0 })} />
        <TextField size="small" fullWidth label="Recompensa" placeholder="Lo que lee tu cliente, p. ej. Producto gratis" value={step.title} disabled={disabled}
          inputProps={{ maxLength: 80 }} onChange={e => onChange({ ...step, title: e.target.value })} />
        {!disabled && (
          <Tooltip title="Quitar recompensa">
            <IconButton onClick={onRemove}><DeleteOutlineIcon fontSize="small" /></IconButton>
          </Tooltip>
        )}
      </Stack>
      <TextField size="small" fullWidth label="Detalle (opcional)" value={step.description} disabled={disabled} sx={{ mt: 1.5 }}
        inputProps={{ maxLength: 300 }} onChange={e => onChange({ ...step, description: e.target.value })} />
      <RulesEditor rules={step.rules} options={options} disabled={disabled} onChange={rules => onChange({ ...step, rules })} />
    </Box>
  );
};

/** /admin/loyalty: the visit roadmap (each visit unlocks the next visit's reward), its rules and how it's doing. */
const Loyalty = () => {
  const axios = useAxios();
  const { membership } = React.useContext(ShellContext);
  const isAdmin = !!membership?.is_admin;

  const [program, setProgram] = React.useState<Program | null>(null);
  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [options, setOptions] = React.useState<Option[]>([]);
  const [business, setBusiness] = React.useState<{ name: string; slug: string; brand?: PublicBrand } | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');
  // Clientes (how far each customer got) or Programa (the route and its rewards). Until chosen: the customers once
  // there are some, the program while it's being set up.
  const [chosenTab, setTab] = React.useState<'customers' | 'program' | null>(null);

  React.useEffect(() => {
    axios.get('api/loyalty/program/')
      // No program yet: the API answers with an empty body.
      .then(r => { const data = r.data || null; setProgram(data); setDraft(toDraft(data)); })
      .catch(err => setError(errorText(err, 'No se pudo cargar el programa.')));
    Promise.allSettled([axios.get('api/categories/'), axios.get('api/products/')]).then(([c, p]) => {
      const categories = c.status === 'fulfilled' ? (c.value.data ?? []) : [];
      const products = p.status === 'fulfilled' ? (p.value.data ?? []).filter((x: any) => x.enabled !== false) : [];
      const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
      setOptions([
        ...categories.sort(byName).map((x: any) => ({ kind: 'categories' as const, id: x.id, name: x.name })),
        ...products.sort(byName).map((x: any) => ({ kind: 'products' as const, id: x.id, name: x.name })),
      ]);
    });
    axios.get('management/organization/').then(r => {
      const org = r.data;
      setBusiness({ name: org.name, slug: org.slug });
      if (!org.slug) return;
      fetch(`${process.env.REACT_APP_BASE_URL}/api/public/businesses/${org.slug}/catalog/`)
        .then(res => (res.ok ? res.json() : null))
        .then(menu => { if (menu?.business?.brand) setBusiness({ name: org.name, slug: org.slug, brand: menu.business.brand }); })
        .catch(() => {});
    }).catch(() => {});
  }, [axios]);

  useFontsStylesheet(business?.brand?.fonts_css);

  const changed = !!draft && JSON.stringify(draft) !== JSON.stringify(toDraft(program));
  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft(d => (d ? { ...d, [key]: value } : d));
  const setStep = (i: number, step: Step) => setDraft(d => d && { ...d, steps: d.steps.map((s, j) => (j === i ? step : s)) });
  const setInvites = (change: Partial<Invites>) => setDraft(d => d && { ...d, invites: { ...d.invites, ...change } });

  const addStep = () => setDraft(d => {
    if (!d) return d;
    const visit = Math.max(1, ...d.steps.map(s => s.visit)) + 1;
    return { ...d, steps: [...d.steps, { visit, title: '', description: '', rules: { requires: null, give: [blank('percent')] } }] };
  });

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const { data } = await axios.put('api/loyalty/program/', { ...draft, steps: [...draft.steps].sort((a, b) => a.visit - b.visit) });
      setProgram(data);
      setDraft(toDraft(data));
      setNotice(data.running ? 'Guardado. La caja ya aplica el programa.' : 'Guardado. El programa está apagado: actívalo cuando quieras empezar.');
    } catch (err) {
      setError(errorText(err, 'No se pudo guardar el programa.'));
    } finally {
      setSaving(false);
    }
  };

  // The card a customer sees after their first visit, with this draft.
  const sorted = draft ? [...draft.steps].sort((a, b) => a.visit - b.visit) : [];
  const sample: Card | null = draft && business ? {
    business,
    customer: { first_name: 'Ana' },
    program: {
      name: draft.name, running: true, starts_on: draft.starts_on, ends_on: draft.ends_on, min_amount: draft.min_amount, reward_days: draft.reward_days,
      total_visits: Math.max(0, ...sorted.map(s => s.visit)), steps: sorted.map((s, i) => ({ id: i + 1, visit: s.visit, title: s.title || 'Recompensa', description: s.description })),
    },
    visits: 1,
    last_visit_day: null,
    rewards: sorted[0]?.visit === 2
      ? [{ id: 1, visit: 2, title: sorted[0].title || 'Recompensa', description: sorted[0].description, expires_at: new Date(Date.now() + draft.reward_days * 864e5).toISOString(), redeemed_at: null, state: 'available' }]
      : [],
    invites: draft.invites.enabled ? {
      path: `/${business.slug}/invita/ana-ejemplo`, per_customer: draft.invites.per_customer, left: draft.invites.per_customer, friends: [],
      welcome: { title: draft.invites.welcome.title || 'Recompensa', description: draft.invites.welcome.description },
      thanks: { title: draft.invites.thanks.title || 'Recompensa', description: draft.invites.thanks.description },
    } : null,
  } : null;

  const stats = program?.stats;
  const disabled = !isAdmin;
  const tab = chosenTab ?? (stats?.members ? 'customers' : 'program');

  return (
    <PageShell
      title="Lealtad"
      subtitle="Premia a tus clientes por visita: cada visita desbloquea la recompensa de la siguiente. Tu cliente se registra con su teléfono al pagar."
      actions={isAdmin && (tab === 'program' || changed) && <Button variant="contained" onClick={save} disabled={!changed || saving}>{saving ? 'Guardando…' : 'Guardar programa'}</Button>}
      toolbar={program && (
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
          <Tab value="customers" label="Clientes" data-testid="loyalty-tab-customers" />
          <Tab value="program" label="Programa" data-testid="loyalty-tab-program" />
        </Tabs>
      )}
    >
      {!isAdmin && <Alert severity="info" sx={{ mb: 3 }}>Solo un administrador puede cambiar el programa de lealtad.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setNotice('')}>{notice}</Alert>}

      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 3 }}><StatCard label="Clientes en el programa" value={number(stats.members)} /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><StatCard label="Visitas (últimos 7 días)" value={number(stats.visits_7d)} /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><StatCard label="Recompensas usadas" value={number(stats.rewards_used)} /></Grid>
          <Grid size={{ xs: 6, md: 3 }}><StatCard label="Recompensas por usar" value={number(stats.rewards_available)} /></Grid>
          {(program?.invites?.enabled || !!stats.invites_joined) && (
            <>
              <Grid size={{ xs: 6, md: 3 }}><StatCard label="Amigos invitados" value={number(stats.invites_joined)} /></Grid>
              <Grid size={{ xs: 6, md: 3 }}><StatCard label="Invitados que ya compraron" value={number(stats.invites_bought)} /></Grid>
            </>
          )}
        </Grid>
      )}

      {program && tab === 'customers' ? <LoyaltyCustomers /> : !draft ? (
        !error && <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress size={28} /></Box>
      ) : (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 7 }}>
            <Stack spacing={3}>
              <SectionCard title="Programa" subtitle={program?.running ? 'Activo: la caja registra visitas y aplica recompensas.' : 'Apagado: la caja no registra visitas.'}>
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' } }}>
                    <TextField fullWidth label="Nombre" value={draft.name} disabled={disabled} inputProps={{ maxLength: 80 }} onChange={e => set('name', e.target.value)} />
                    <FormControlLabel sx={{ flexShrink: 0 }} label="Activo"
                      control={<Switch checked={draft.active} disabled={disabled} onChange={e => set('active', e.target.checked)} />} />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField fullWidth type="date" label="Empieza (opcional)" value={draft.starts_on ?? ''} disabled={disabled}
                      slotProps={{ inputLabel: { shrink: true } }} onChange={e => set('starts_on', e.target.value || null)} />
                    <TextField fullWidth type="date" label="Termina (opcional)" value={draft.ends_on ?? ''} disabled={disabled}
                      slotProps={{ inputLabel: { shrink: true } }} onChange={e => set('ends_on', e.target.value || null)} />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <NumberField fullWidth label="Compra mínima para contar la visita ($)" value={draft.min_amount} disabled={disabled}
                      helperText="Cuenta una visita por día por cliente; la recompensa cuenta como compra." onChange={v => set('min_amount', v ?? 0)} />
                    <NumberField fullWidth label="Días para usar cada recompensa" value={draft.reward_days} decimals={false} disabled={disabled}
                      helperText="Cada recompensa es personal; se usa una por compra." onChange={v => set('reward_days', v ?? 0)} />
                  </Stack>
                </Stack>
              </SectionCard>

              <SectionCard title="Ruta de visitas" subtitle="La recompensa de una visita se desbloquea con la anterior y se aplica en la caja.">
                <Stack spacing={2}>
                  <Box sx={{ border: `1px dashed ${TRUE_COLORS.borderSolid}`, borderRadius: 2, p: 2 }}>
                    <Typography sx={{ fontWeight: 600 }}>Visita 1 · Registro</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Tu cliente da su teléfono al pagar. Su ticket le dice qué gana en su siguiente visita, con el enlace a su tarjeta.
                    </Typography>
                  </Box>
                  {draft.steps.map((step, i) => (
                    <StepEditor key={i} step={step} options={options} disabled={disabled}
                      onChange={s => setStep(i, s)} onRemove={() => set('steps', draft.steps.filter((_, j) => j !== i))} />
                  ))}
                  {isAdmin && (
                    <Button startIcon={<AddIcon />} onClick={addStep} sx={{ alignSelf: 'flex-start' }}>Agregar recompensa</Button>
                  )}
                </Stack>
              </SectionCard>

              <SectionCard title="Invitaciones"
                subtitle="Cada cliente comparte su enlace desde su tarjeta. Su amigo, nuevo en tu negocio, recibe su recompensa al registrarse; quien invita recibe la suya cuando su amigo hace su primera compra.">
                <Stack spacing={2}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' } }}>
                    <FormControlLabel label="Activas" sx={{ flexShrink: 0 }}
                      control={<Switch checked={draft.invites.enabled} disabled={disabled} onChange={e => setInvites({ enabled: e.target.checked })} />} />
                    <NumberField size="small" label="Invitaciones por cliente" value={draft.invites.per_customer} decimals={false} disabled={disabled}
                      sx={{ width: { sm: 220 } }} onChange={v => setInvites({ per_customer: v ?? 0 })} />
                  </Stack>
                  <InviteRewardEditor label="Para tu cliente nuevo" hint="La recibe al registrarse con el enlace; la usa en su primera compra."
                    reward={draft.invites.welcome} options={options} disabled={disabled} onChange={welcome => setInvites({ welcome })} />
                  <InviteRewardEditor label="Para quien invita" hint={`La recibe cuando su amigo hace su primera compra de ${currency(draft.min_amount)} o más.`}
                    reward={draft.invites.thanks} options={options} disabled={disabled} onChange={thanks => setInvites({ thanks })} />
                </Stack>
              </SectionCard>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }}>
            <Box sx={{ position: { lg: 'sticky' }, top: { lg: 96 } }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                Así ve su tarjeta un cliente después de su primera visita {changed ? '(sin guardar)' : ''}
              </Typography>
              <Box sx={{
                width: '100%', maxWidth: 390, height: 680, mx: 'auto', overflowY: 'auto', overflowX: 'hidden', borderRadius: '28px',
                border: `8px solid ${TRUE_COLORS.primary}`, boxShadow: '0 12px 40px rgba(20,37,44,0.18)', bgcolor: '#fff',
              }}>
                {sample ? <CardView card={sample} embedded cardUrl={`${window.location.origin}/${business?.slug}/mi/ejemplo`} />
                  : <Box sx={{ height: '100%', display: 'grid', placeItems: 'center' }}><CircularProgress size={24} /></Box>}
              </Box>
            </Box>
          </Grid>
        </Grid>
      )}
    </PageShell>
  );
};

export default Loyalty;
